import ExcelJS from 'exceljs';

/**
 * Converts TaskSetProfiles to Excel spreadsheet format.
 * Each profile gets its own sheet with tasks and subtasks.
 * Reference sheets for Teams and Templates provide lookup data.
 */
class TaskSetProfileToSpreadsheetConverter {
  constructor() {
    this.workbook = new ExcelJS.Workbook();
    this.maxDepth = 0;
    this.DEFAULT_TASK_LEVELS = 5;
    // Excel supports at most 8 row grouping levels (outlineLevel 0-7)
    this.MAX_ROW_OUTLINE_LEVEL = 7;
    this.LOCKED_BACKGROUND = 'E0E0E0';
    this.SUBTASK_BACKGROUND = 'F5F5F5';
    // Store counts for data validation ranges
    this.teamsCount = 0;
    this.templatesCount = 0;
    // Export options with defaults
    this.options = {
      taskLevels: 5,
      includeTaskCodes: true,
      includeLeadTeam: true,
      includeWeighting: true,
      includeVersionReferences: true
    };
  }

  /**
   * Process multiple TaskSetProfiles into a single workbook.
   * Each profile gets its own sheet named "{code} | {title}".
   * @param {Array} taskSetProfiles - Array of TaskSetProfile objects
   * @param {Array} teams - Array of team objects for reference sheet
   * @param {Array} templates - Array of template objects for reference sheet
   * @param {Object} options - Export options
   * @param {number} options.taskLevels - Number of task level columns (default: 5)
   * @param {boolean} options.includeTaskCodes - Include Task Codes column (default: true)
   * @param {boolean} options.includeLeadTeam - Include Lead Team ID column (default: true)
   * @param {boolean} options.includeWeighting - Include Weighting column (default: true)
   * @param {boolean} options.includeVersionReferences - Include VersionReference columns (default: true)
   * @returns {Promise<ArrayBuffer>} Excel file as ArrayBuffer
   */
  async process(taskSetProfiles, teams = [], templates = [], options = {}) {
    // Merge options with defaults
    this.options = { ...this.options, ...options };

    // Store counts for data validation
    this.teamsCount = teams.length;
    this.templatesCount = templates.length;

    // Build lookup maps for consistent formatting
    this.teamLookup = new Map();
    teams.forEach(team => {
      const name = team.name || '';
      const id = team.id || '';
      this.teamLookup.set(id, `${name} (${id})`);
    });

    this.templateLookup = new Map();
    templates.forEach(template => {
      const type = template.type || template.itemType || '';
      const code = template.code || '';
      const title = template.title || '';
      // Key by "code - type" to match both fields
      const key = `${code}|${type}`;
      this.templateLookup.set(key, `${title} (${code} - ${type})`);
    });

    // Create one sheet per profile
    for (const profile of taskSetProfiles) {
      this.createProfileSheet(profile);
    }

    // Create lookup/reference sheets at the end
    this.createTeamsSheet(teams);
    this.createTemplatesSheet(templates);

    return await this.workbook.xlsx.writeBuffer();
  }

  /**
   * Process a single TaskSetProfile.
   * @param {Object} taskSetProfile - Single TaskSetProfile object
   * @param {Array} teams - Array of team objects for reference sheet
   * @param {Array} templates - Array of template objects for reference sheet
   * @returns {Promise<ArrayBuffer>} Excel file as ArrayBuffer
   */
  async processSingle(taskSetProfile, teams = [], templates = []) {
    return this.process([taskSetProfile], teams, templates);
  }

  /**
   * Create a worksheet for a single profile.
   * @param {Object} profile - TaskSetProfile object
   * @returns {ExcelJS.Worksheet} The created worksheet
   */
  createProfileSheet(profile) {
    // Truncate sheet name to 31 chars (Excel limit)
    const sheetName = this.truncateSheetName(`${profile.code} | ${profile.title}`);

    // Find max depth for this profile
    this.maxDepth = this.findMaxDepth(profile.taskProfiles || []);

    // Flatten rows first to decide whether grouping fits Excel's limit.
    // Rows nested deeper than the limit would write out-of-spec outline
    // values that Excel clamps or repairs, so grouping is omitted entirely
    // in that case — the task level columns still carry the hierarchy.
    const rows = this.flattenTaskProfiles(profile.taskProfiles || []);
    const useRowGrouping = rows.every(rowData => rowData.level <= this.MAX_ROW_OUTLINE_LEVEL);

    const worksheet = this.workbook.addWorksheet(sheetName, {
      properties: {
        outlineLevelCol: 1,
        outlineLevelRow: useRowGrouping ? 1 : 0,
        defaultRowHeight: 15
      }
    });

    worksheet.columns = this.buildColumns();

    // Add header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };

    rows.forEach(rowData => {
      const row = worksheet.addRow(rowData.data);
      if (useRowGrouping) {
        row.outlineLevel = rowData.level;
      }

      // Apply styling
      this.applyRowStyling(row, rowData);
    });

    // Apply data validation for dropdown columns
    this.applyDataValidation(worksheet, rows.length);

    worksheet.properties.outlineProperties = {
      summaryBelow: false,
      summaryRight: false
    };

    return worksheet;
  }

  /**
   * Truncate sheet name to Excel's 31 character limit.
   * @param {string} name - Original sheet name
   * @returns {string} Truncated sheet name
   */
  truncateSheetName(name) {
    if (name.length <= 31) return name;
    return name.substring(0, 31);
  }

  /**
   * Build column definitions for profile sheets.
   * @returns {Array} Column definitions
   */
  buildColumns() {
    const taskLevels = this.options.taskLevels || this.DEFAULT_TASK_LEVELS;

    const columns = [
      { header: 'Code', key: 'code', width: 10 },
    ];

    // Task Level columns
    for (let i = 0; i < taskLevels; i++) {
      columns.push({
        header: `Task Level ${i + 1}`,
        key: `taskLevel_${i}`,
        width: 25,
        style: { font: { name: 'Consolas' } }
      });
    }

    // Conditionally add Task Codes column
    if (this.options.includeTaskCodes) {
      columns.push({ header: 'Task Codes', key: 'taskCodes', width: 20 });
    }

    columns.push(
      { header: 'Subtask Title', key: 'subtask_title', width: 25 }
    );

    // Conditionally add Lead Team column
    if (this.options.includeLeadTeam) {
      columns.push({ header: 'Lead Team', key: 'subtask_defaultLeadByTeamId', width: 40 });
    }

    // Conditionally add Weighting column
    if (this.options.includeWeighting) {
      columns.push({ header: 'Weighting', key: 'subtask_weighting', width: 10 });
    }

    columns.push(
      { header: 'Template', key: 'subtask_template', width: 40 }
    );

    // Conditionally add Version Reference columns
    if (this.options.includeVersionReferences) {
      columns.push(
        { header: 'VersionReference', key: 'versionReference', width: 15 },
        { header: 'Subtask VersionRef', key: 'subtask_versionReference', width: 15 }
      );
    }

    return columns;
  }

  /**
   * Flatten task profiles into rows with hierarchy information.
   * @param {Array} taskProfiles - Array of TaskProfile objects
   * @param {number} depth - Current depth in hierarchy
   * @returns {Array} Flattened rows with data and metadata
   */
  flattenTaskProfiles(taskProfiles, depth = 0) {
    const rows = [];

    taskProfiles.forEach(taskProfile => {
      // Add task row
      const taskRow = this.createTaskRow(taskProfile, depth);
      rows.push(taskRow);

      // Add subtask rows for this task
      if (taskProfile.subtaskProfiles?.length > 0) {
        taskProfile.subtaskProfiles.forEach(subtask => {
          rows.push(this.createSubtaskRow(subtask, depth));
        });
      }

      // Process children recursively
      if (taskProfile.children?.length > 0) {
        const childRows = this.flattenTaskProfiles(taskProfile.children, depth + 1);
        rows.push(...childRows);
      }
    });

    return rows;
  }

  /**
   * Create a row object for a task.
   * @param {Object} taskProfile - TaskProfile object
   * @param {number} depth - Depth in hierarchy
   * @returns {Object} Row object with data, level, and type
   */
  createTaskRow(taskProfile, depth) {
    const taskLevels = this.options.taskLevels || this.DEFAULT_TASK_LEVELS;

    const data = {
      code: taskProfile.code || '',
    };

    // Conditionally include version reference
    if (this.options.includeVersionReferences) {
      data.versionReference = taskProfile.versionReference || '';
    }

    // Set task title in appropriate level column
    for (let i = 0; i < taskLevels; i++) {
      data[`taskLevel_${i}`] = (i === depth) ? taskProfile.title : '';
    }

    // Conditionally include Task Codes
    if (this.options.includeTaskCodes) {
      data.taskCodes = taskProfile.taskCodes?.join(', ') || '';
    }

    // Clear subtask columns for task rows
    data.subtask_title = '';
    if (this.options.includeVersionReferences) {
      data.subtask_versionReference = '';
    }
    if (this.options.includeLeadTeam) {
      data.subtask_defaultLeadByTeamId = '';
    }
    if (this.options.includeWeighting) {
      data.subtask_weighting = '';
    }
    data.subtask_template = '';

    return { data, level: depth, type: 'task' };
  }

  /**
   * Create a row object for a subtask.
   * @param {Object} subtaskProfile - SubtaskProfile object
   * @param {number} parentDepth - Depth of parent task
   * @returns {Object} Row object with data, level, and type
   */
  createSubtaskRow(subtaskProfile, parentDepth) {
    const taskLevels = this.options.taskLevels || this.DEFAULT_TASK_LEVELS;

    const data = {
      code: '',
    };

    // Conditionally include version references
    if (this.options.includeVersionReferences) {
      data.versionReference = '';
    }

    // Clear all task level columns for subtask rows
    for (let i = 0; i < taskLevels; i++) {
      data[`taskLevel_${i}`] = '';
    }

    // Conditionally include Task Codes
    if (this.options.includeTaskCodes) {
      data.taskCodes = '';
    }

    data.subtask_title = subtaskProfile.title || '';
    if (this.options.includeVersionReferences) {
      data.subtask_versionReference = subtaskProfile.versionReference || '';
    }

    // Conditionally include Lead Team (use lookup for consistent format)
    if (this.options.includeLeadTeam) {
      const teamId = subtaskProfile.defaultLeadByTeamId || '';
      data.subtask_defaultLeadByTeamId = this.teamLookup?.get(teamId) || teamId;
    }

    // Conditionally include Weighting
    if (this.options.includeWeighting) {
      data.subtask_weighting = subtaskProfile.weighting ?? '';
    }

    // Template (use lookup for consistent format with dropdown)
    const templateCode = subtaskProfile.itemTemplateCode || '';
    const itemType = subtaskProfile.itemType || '';
    const templateKey = `${templateCode}|${itemType}`;
    data.subtask_template = this.templateLookup?.get(templateKey) || '';

    return { data, level: parentDepth + 1, type: 'subtask' };
  }

  /**
   * Find the maximum depth of task hierarchy.
   * @param {Array} taskProfiles - Array of TaskProfile objects
   * @param {number} currentDepth - Current depth
   * @returns {number} Maximum depth
   */
  findMaxDepth(taskProfiles, currentDepth = 0) {
    let maxDepth = currentDepth;
    taskProfiles.forEach(tp => {
      if (tp.children?.length > 0) {
        const childDepth = this.findMaxDepth(tp.children, currentDepth + 1);
        maxDepth = Math.max(maxDepth, childDepth);
      }
    });
    return maxDepth;
  }

  /**
   * Apply styling to a row based on its type.
   * @param {ExcelJS.Row} row - Excel row object
   * @param {Object} rowData - Row metadata
   */
  applyRowStyling(row, rowData) {
    const taskLevels = this.options.taskLevels || this.DEFAULT_TASK_LEVELS;

    // Apply Consolas font to task level cells with content
    for (let i = 0; i < taskLevels; i++) {
      const cell = row.getCell(`taskLevel_${i}`);
      if (cell.value) {
        cell.font = { name: 'Consolas' };
      }
    }

    // Light background for subtask rows to distinguish them
    if (rowData.type === 'subtask') {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: this.SUBTASK_BACKGROUND }
        };
      });
    }
  }

  /**
   * Apply data validation (dropdowns) for Lead Team and Template columns.
   * @param {ExcelJS.Worksheet} worksheet - The worksheet to apply validation to
   * @param {number} rowCount - Number of data rows (excluding header)
   */
  applyDataValidation(worksheet, rowCount) {
    if (rowCount === 0) return;

    const startRow = 2; // Skip header
    const endRow = rowCount + 1;

    // Lead Team dropdown (if column exists and teams available)
    if (this.options.includeLeadTeam && this.teamsCount > 0) {
      const teamCol = worksheet.getColumn('subtask_defaultLeadByTeamId');
      if (teamCol) {
        const teamsEndRow = this.teamsCount + 1;
        for (let row = startRow; row <= endRow; row++) {
          const cell = worksheet.getCell(row, teamCol.number);
          cell.dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`Teams!$A$2:$A$${teamsEndRow}`]
          };
        }
      }
    }

    // Template dropdown (if templates available)
    if (this.templatesCount > 0) {
      const templateCol = worksheet.getColumn('subtask_template');
      if (templateCol) {
        const templatesEndRow = this.templatesCount + 1;
        for (let row = startRow; row <= endRow; row++) {
          const cell = worksheet.getCell(row, templateCol.number);
          cell.dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`Templates!$A$2:$A$${templatesEndRow}`]
          };
        }
      }
    }
  }

  /**
   * Create the Teams reference sheet.
   * @param {Array} teams - Array of team objects
   */
  createTeamsSheet(teams) {
    const sheet = this.workbook.addWorksheet('Teams');
    sheet.columns = [
      { header: 'Team', key: 'team', width: 50 }
    ];

    // Build and sort team entries alphanumerically
    const teamEntries = teams.map(team => {
      const name = team.name || '';
      const id = team.id || '';
      return `${name} (${id})`;
    }).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

    teamEntries.forEach(combined => {
      sheet.addRow({ team: combined });
    });

    // Format header
    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: this.LOCKED_BACKGROUND }
    };
  }

  /**
   * Create the Templates reference sheet.
   * @param {Array} templates - Array of template objects
   */
  createTemplatesSheet(templates) {
    const sheet = this.workbook.addWorksheet('Templates');
    sheet.columns = [
      { header: 'Template', key: 'template', width: 60 }
    ];

    // Build and sort template entries alphanumerically
    const templateEntries = templates.map(template => {
      const type = template.type || template.itemType || '';
      const code = template.code || '';
      const title = template.title || '';
      return `${title} (${code} - ${type})`;
    }).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

    templateEntries.forEach(combined => {
      sheet.addRow({ template: combined });
    });

    // Format header
    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: this.LOCKED_BACKGROUND }
    };
  }
}

export default TaskSetProfileToSpreadsheetConverter;
